/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Button, Form, FormGroup } from 'react-bootstrap'
import { Formik } from 'formik'
import { useServiceContext } from '~/root'
import { useModal } from '~/contexts/ModalContext'
import Modal from '~/components/Modal'
import { Select } from '~/components/Select/Select'
import Field from '~/components/shared/Field'
import { ErrorMessageStyled } from '~/components/ErrorMessage'
import FsiPropertiesModal from '~/pages/browser/properties/FsiPropertiesModal'
import { useEsignature } from '~/hooks/useEsignature'
import { useTranslation } from 'react-i18next'
import { MasterDataType, type MasterDataTypeValue } from '~/constants/etmf'
import { getLinkedEntities } from '~/services/api/etmf'
import { getById as getMetadataById } from '~/services/api/metadata'
import { createMasterDataFile } from '~/services/api/etmf'
import type { OptionType } from '~/components/Select/Select'

interface AddMasterDataModalProps {
  type: MasterDataTypeValue
  onClose?: () => void
}

interface FormValues {
  filename: string
  country: OptionType | null
}

const AddMasterDataModal: React.FC<AddMasterDataModalProps> = ({ type, onClose }) => {
  const context = useServiceContext()
  const { t } = useTranslation()
  const { signAction } = useEsignature()
  const { showModal } = useModal()
  const { toast, auth } = context.service
  const { constant, $rootScope } = context

  const defaultFilterConst: FormValues =
    type === MasterDataType.COUNTRY
      ? {
          filename: '',
          country: null
        }
      : { filename: '', country: null }

  const [isOpen, setIsOpen] = useState(false)
  const [countryPossibleValues, setCountryPossibleValues] = useState<OptionType[]>([])
  const [typeReadable, setTypeReadable] = useState('')

  useEffect(() => {
    setIsOpen(true)
    loadData()
  }, [type])

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const loadData = async () => {
    let readableType = ''

    switch (type) {
      case MasterDataType.ENVIRONMENT:
        readableType = t('EMTF_MD_ENVIRONMENT')
        break
      case MasterDataType.PRODUCT:
        readableType = t('EMTF_MD_PRODUCT')
        break
      case MasterDataType.TRIAL:
        readableType = t('EMTF_MD_TRIAL')
        break
      case MasterDataType.COUNTRY:
        readableType = t('EMTF_MD_COUNTRY')
        try {
          const linkedEntitiesData = await getLinkedEntities()
          const countryMetadataId = linkedEntitiesData.entityLinkMap.COUNTRY
          const metadataData = await getMetadataById(Number(countryMetadataId))
          if (metadataData?.definition?.options) {
            const options = metadataData.definition.options.map((opt: string) => ({
              value: opt,
              label: opt
            }))
            setCountryPossibleValues(options)
          }
        } catch (error) {
          console.error('Error loading country options:', error)
        }
        break
      case MasterDataType.SITE:
        readableType = t('EMTF_MD_SITE')
        break
    }

    setTypeReadable(readableType)
  }

  const save = async (values: FormValues) => {
    doClose()

    const doSaveCallback = async (validation?: Record<string, unknown>) => {
      try {
        const data = await createMasterDataFile(type, values.filename.trim(), validation, {
          countryName: values.country?.value || null
        })

        toast.success(t('ETMF_ADD_MD_MSG_SUCCESS'))
        window.dispatchEvent(new CustomEvent('bctable:updateTable', { detail: true }))
        window.dispatchEvent(new CustomEvent('updateSearchFiles'))

        if (data?.elements?.[0]?.id) {
          showModal(<FsiPropertiesModal fileId={data.elements[0].id} />)
        }
      } catch (error: any) {
        toast.error(t('{{error}}', { error: error?.data?.message || error?.message }))
      }
    }

    if (auth.requireESignatureForFileCommands()) {
      const validation = await signAction('ADMIN_ACTION', t('ADMIN_ACTION_ETMF_ADD_MD'))
      return doSaveCallback(validation as Record<string, unknown>)
    } else {
      return doSaveCallback()
    }
  }

  const validate = (values: FormValues) => {
    const errors: Partial<Record<keyof FormValues, string>> = {}

    if (!values.filename || values.filename.trim() === '') {
      errors.filename = 'Required'
    }

    if (type === MasterDataType.COUNTRY && !values.country) {
      errors.country = 'Required'
    }

    return errors
  }

  return (
    <Formik
      validate={validate}
      enableReinitialize={true}
      initialValues={defaultFilterConst}
      onSubmit={(values) => {
        save(values)
      }}
    >
      {({ values, handleSubmit, setFieldValue }) => (
        <Modal show={isOpen} handleClose={doClose} title={`${t('ETMF_ADD_MD_TITLE')} ${typeReadable}`}>
          <Modal.Body>
            <div className="form-group w50p">
              <FormGroup controlId="66eea875-6ac0-4dff-aebc-00c7419e63e2" className="mb10">
                <Form.Label className="fs12 mb0">{t('ETMF_ADD_MD_LABEL_NAME')}</Form.Label>
                <Field
                  type="text"
                  className="mb0 w100p pos-relative"
                  name="filename"
                  maxLength={255}
                  id="in-filename4"
                />
                <ErrorMessageStyled name="filename" classesName="fs14" />
              </FormGroup>

              {type === MasterDataType.COUNTRY && (
                <FormGroup controlId="28e6b0da-201b-464b-8af6-c676f8de3f27">
                  <Form.Label className="fs12 mb0">{t('ETMF_ADD_MD_LABEL_COUNTRY')}</Form.Label>
                  <Select
                    placeholder={t('ETMF_LABEL_SELECT_COUNTRY')}
                    name="country"
                    value={values.country?.value || null}
                    options={countryPossibleValues}
                    onChange={(selectedCountry) => {
                      setFieldValue('country', selectedCountry || null)
                    }}
                    multi={false}
                    showNewOptionAtTop={true}
                    isClearable={false}
                    id="fc887545-00f0-4f58-9933-10ccdb7e56b1"
                  />
                  <ErrorMessageStyled name="country" classesName="fs14" />
                </FormGroup>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button data-action="ok" variant="link" onClick={doClose} id="9bebd85e-bf17-43a1-b9ae-9635f26c056d">
              {t('CANCEL')}
            </Button>
            <Button
              data-action="ok"
              className="btn btn-primary"
              onClick={() => handleSubmit()}
              id="60c1b3ff-b6f9-4968-ad6c-6afbd0b4a336"
            >
              {t('SAVE_BTN')}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Formik>
  )
}

export default AddMasterDataModal
